import { useEffect, useRef, useState } from "react";
import RankPageFilter from "src/components/admin/rankManagement/RankPageFilter";
import moment from "moment";
import StudentDetails from "src/components/admin/rankManagement/StudentDetails";
import { RedirectButton } from "src/components/common/Button/BackButton";
import ClassDetails from "src/components/admin/rankManagement/ClassDetails.js";
import { Button } from "react-bootstrap";
import useRegularClassSchedule from "src/hooks/useRegularClassSchedule";
import { useMemberClassesQuery } from "src/services/react-query-apis/get-member-classes";
import { useMembersQuery } from "src/services/react-query-apis/get-members";
import { useQueryClient } from "@tanstack/react-query";
import * as putMemberClasses from "src/services/react-query-apis/put-member-classes";
import * as postMemberClassesAssignAndUpdate from "src/services/react-query-apis/post-member-classes-assign-and-update";
import deepCopy from "src/utils/deepCopy";
import { swalError, swalSuccess } from "src/utils/swalHelpers";

const processedDate = localStorage.getItem("current_month");

export const monthsInfo = {
  前月: moment(processedDate).subtract(1, "months").format("YYYY-MM"),
  当月: moment(processedDate).format("YYYY-MM"),
  翌月: moment(processedDate).add(1, "months").format("YYYY-MM"),
};

export const previousMonth = selectedMonth => {
  if (selectedMonth === "当月") return "前月";
  if (selectedMonth === "翌月") return "当月";
  return null;
};

const RankHistoryBody = () => {
  const [selectedMemberCode, setSelectedMemberCode] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState("当月");

  const searchInputRef = useRef(null);

  const queryClient = useQueryClient();

  const paddedMemberCode = selectedMemberCode?.padStart(7, "0");

  const membersQuery = useMembersQuery({
    per_page: 10,
    search: paddedMemberCode,
    queryConfig: {
      enabled: !!selectedMemberCode,
      staleTime: 0,
    },
  });

  let memberData = membersQuery.data?.data?.[0];
  memberData = memberData?.member_code === paddedMemberCode && memberData;

  useEffect(() => {
    (async () => {
      if (
        !membersQuery.isPending &&
        !membersQuery.isError &&
        !memberData &&
        paddedMemberCode
      ) {
        await swalError(`[${paddedMemberCode}]会員番号は存在しません`);
        focusInput();
      }
    })();
  }, [membersQuery, memberData, paddedMemberCode]);

  const focusInput = () => {
    setTimeout(() => {
      searchInputRef.current.focus();
    });
  };

  return (
    <div className="card rank-history">
      <div className="card-header d-flex justify-content-between">
        <button className="btn px-3 mr-4 btn-primary">進級処理</button>
        <RedirectButton
          redirectTo="/admin/dashboard"
          className="btn px-3 mr-4 btn-primary"
        >
          戻る
        </RedirectButton>
      </div>
      <div className="card-body">
        <RankPageFilter
          searchInputRef={searchInputRef}
          selectedMonth={selectedMonth}
          onMonth={month => {
            setSelectedMonth(month);
          }}
          onSearch={async value => {
            setSelectedMemberCode(value);
            await queryClient.invalidateQueries({ queryKey: ["members"] });
          }}
          isSearching={membersQuery.isFetching}
        />
        <StudentDetails memberData={memberData} />
        {!membersQuery.isFetching && memberData && (
          <MonthsClassDetails
            key={selectedMonth}
            memberData={memberData}
            selectedMonth={selectedMonth}
            focusInput={focusInput}
            setSelectedMemberCode={setSelectedMemberCode}
          />
        )}
      </div>
    </div>
  );
};

const MonthsClassDetails = ({
  memberData,
  selectedMonth,
  focusInput,
  setSelectedMemberCode,
}) => {
  const [classData, setClassData] = useState(null);

  const regularClassSchedule = useRegularClassSchedule();

  const startDate = monthsInfo["前月"];
  const endDate = monthsInfo[selectedMonth];

  const memberClassesQuery = useMemberClassesQuery({
    member_id: memberData?.id,
    start_date: startDate,
    end_date: endDate,
    closest: 0,
    queryConfig: {
      enabled: !!memberData?.id,
      staleTime: 0,
      refetchOnMount: true,
    },
  });

  const mutationConfig = {
    onSuccess: async data => {
      setClassData(null);
      setSelectedMemberCode(null);
      await swalSuccess(data.message);
      focusInput();
    },
  };

  const memberClassesAssignAndUpdateMutation =
    postMemberClassesAssignAndUpdate.usePostMemberClassesAssignAndUpdateMutation(
      {
        mutationConfig,
      }
    );

  const memberClassesUpdateMutation =
    putMemberClasses.usePutMemberClassesMutation({
      mutationConfig,
    });

  useEffect(() => {
    (async () => {
      if (
        !memberClassesQuery.isFetching &&
        !memberClassesQuery.isError &&
        memberClassesQuery.data
      ) {
        const selectedMonthData = memberClassesQuery?.data.data?.filter(
          val => val.processed_date === monthsInfo[selectedMonth]
        );

        if (selectedMonthData?.length === 0) {
          const prevMonthData = memberClassesQuery?.data.data?.filter(
            val =>
              val.processed_date === monthsInfo[previousMonth(selectedMonth)]
          );

          if (prevMonthData.length === 0) {
            await swalError("クラスデータがない");
            setClassData(null);
            focusInput();
          } else {
            const copiedClasses = prevMonthData.map(item => ({
              ...item,
              id: -Math.abs(item.id), // ensures negative
              processed_date: monthsInfo[selectedMonth], // set to current month
            }));

            const allClasses = [
              ...memberClassesQuery.data.data,
              ...copiedClasses,
            ];
            setClassData(allClasses);
          }
        } else {
          setClassData(memberClassesQuery?.data?.data);
        }
      }
    })();
  }, [
    memberClassesQuery.isFetching,
    memberClassesQuery.data,
    memberClassesQuery.isError,
    selectedMonth,
    focusInput,
  ]);

  //TODO: youd want to use useRegularClassScheduleQuery here
  const getMatchingSchedule = (schedule, rank) => {
    const schedules = regularClassSchedule?.list
      ?.find(val => val?.regular_class_id === schedule?.regular_class_id)
      ?.classSchedule?.find(val => val?.day === schedule?.day_week)
      ?.classes?.find(
        val =>
          val?.start_time === schedule?.start_time &&
          val?.end_time === schedule?.end_time &&
          val?.class_level?.from_level <= rank &&
          val?.class_level?.to_level >= rank &&
          (!val?.valid_before ||
            moment(val?.valid_before).isAfter(
              monthsInfo[selectedMonth],
              "month"
            )) &&
          moment(val?.valid_from).isSameOrBefore(
            monthsInfo[selectedMonth],
            "month"
          )
      );

    return schedules;
  };

  // this function is required for updating class_level_ when theres a schedule change for an existing class (as opposed to a copied class)
  const validateAndUpdateClassSchedules = classList => {
    for (let i = 0; i < classList.length; i++) {
      const classInfo = classList[i];

      if (classInfo?.regular_class.rank_flag === 1) {
        for (let num = 1; num <= 5; num++) {
          const oldSchedule = classInfo?.[`regular_class_schedule_${num}`];
          if (!oldSchedule) break;

          const matchingSchedule = getMatchingSchedule(
            oldSchedule,
            classInfo?.class
          );

          if (!matchingSchedule) {
            return false;
          }

          classInfo[`class_level_${num}`] = matchingSchedule?.id;
        }
      }
    }

    return true;
  };

  return (
    classData && (
      <form
        onSubmit={async e => {
          e.preventDefault();

          const updatedClassData = deepCopy(
            classData.filter(
              val => val.processed_date === monthsInfo[selectedMonth]
            )
          );

          const validated = validateAndUpdateClassSchedules(updatedClassData);

          if (!validated) {
            await swalError("合致するコマ枠がありません");
            focusInput();
            return;
          }

          const isCopied = updatedClassData.every(({ id }) => id < 0);

          if (isCopied) {
            const payload = postMemberClassesAssignAndUpdate.generatePayload(
              updatedClassData,
              memberData.id,
              monthsInfo[selectedMonth]
            );
            memberClassesAssignAndUpdateMutation.mutate(payload);
          } else {
            const payload = putMemberClasses.generatePayload(
              updatedClassData,
              memberData.id,
              monthsInfo[selectedMonth]
            );

            memberClassesUpdateMutation.mutate(payload);
          }
        }}
      >
        {Object.entries(monthsInfo)
          .filter(([_, date]) => {
            return moment(date).isSameOrBefore(moment(endDate), "month");
          })
          .map(([monthLabel, date]) => {
            const classes = classData.filter(val => {
              return val.processed_date === date;
            });

            return (
              <ClassDetails
                key={monthLabel}
                monthLabel={monthLabel}
                isSelected={monthLabel === selectedMonth}
                date={date}
                classes={classes}
                onRankChange={(classId, rank) => {
                  setClassData(
                    classData.map(val =>
                      val.id === classId ? { ...val, class: rank } : val
                    )
                  );
                }}
              />
            );
          })}
        <Button
          className="float-right col-md-1 mt-2"
          variant="primary"
          type="submit"
          style={{ float: "right" }}
          disabled={
            memberClassesUpdateMutation.isPending ||
            memberClassesAssignAndUpdateMutation.isPending
          }
        >
          保存
        </Button>
      </form>
    )
  );
};

export default RankHistoryBody;
